import { useState } from "react";
import { Search, Globe, Users, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

interface PublicPond {
  id: string;
  name: string;
  type: string;
  is_public: boolean;
  owner_id: string | null;
  parent_id: string | null;
}

interface PublicPondsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSearch: (searchTerm: string) => Promise<PublicPond[]>;
  onJoinPond: (pondId: string) => Promise<void>;
}

export const PublicPondsDialog = ({
  open,
  onOpenChange,
  onSearch,
  onJoinPond,
}: PublicPondsDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<PublicPond[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    try {
      const ponds = await onSearch(searchTerm);
      setResults(ponds);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Explorar Lagoas Públicas
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-9"
              />
            </div>
            <Button onClick={handleSearch} disabled={loading || !searchTerm.trim()}>
              Buscar
            </Button>
          </div>

          <ScrollArea className="h-[400px] pr-4">
            {results.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-12">
                <Globe className="h-12 w-12 mb-4 opacity-50" />
                <p>Busque por lagoas públicas para encontrar comunidades</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((pond) => (
                  <div
                    key={pond.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">{pond.name}</h4>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            Pública
                          </Badge>
                          {pond.parent_id && (
                            <Badge variant="outline" className="text-xs">
                              Sub-lagoa
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        onJoinPond(pond.id);
                        onOpenChange(false);
                      }}
                    >
                      Entrar
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
